import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// A 401 means the session expired or was never established. Send the user to
// the login screen rather than letting pages render empty and look broken.
// The check on the current path stops a redirect loop when /auth/me 401s on
// the login page itself, which is its normal state.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export interface Call {
  id: number;
  call_sid: string;
  from_number: string;
  to_number: string;
  tracking_number: string;
  status: string;
  duration: number;
  recording_url?: string;
  // 'outbound-dialer' for calls placed from the Dialer; null/absent for
  // inbound. Analytics excludes outbound from the answer rate.
  source?: string | null;
  started_at: string;
  ended_at?: string;
  campaign_name?: string;
  utm_source?: string;
  utm_campaign?: string;
  landing_page?: string;
}

export interface TrackingNumber {
  id: number;
  phone_number: string;
  campaign_name: string;
  forward_to: string | null;
  is_active: boolean;
  record_calls: boolean;
  whisper_enabled: boolean;
  created_at: string;
  pending_port: boolean;
}

export interface DashboardData {
  summary: {
    totalCalls: number;
    answeredCalls: number;
    missedCalls: number;
    answerRate: string;
    avgDuration: number;
  };
  charts: {
    callsByHour: Array<{ hour: string; count: number }>;
    callsByCampaign: Array<{
      campaign_name: string;
      total_calls: number;
      answered_calls: number;
      avg_duration: number;
    }>;
    topSources: Array<{
      utm_source: string;
      calls: number;
      answered: number;
    }>;
  };
}

export const callsApi = {
  getCalls: (params?: {
    page?: number;
    limit?: number;
    campaign?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
  }) => api.get('/calls', { params }),

  getCall: (id: number) => api.get(`/calls/${id}`),

  addCallAnalytics: (id: number, analytics: {
    visitorId?: string;
    referrer?: string;
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
    landingPage?: string;
  }) => api.post(`/calls/${id}/analytics`, analytics),
};

export const numbersApi = {
  getNumbers: () => api.get('/numbers'),

  createNumber: (data: {
    phoneNumber: string;
    campaignName: string;
    forwardTo: string;
    recordCalls?: boolean;
    whisperEnabled?: boolean;
  }) => api.post('/numbers', data),

  // forwardTo accepts null: clearing a destination is a real operation, and a
  // number imported from Twilio has none until someone sets one.
  // pendingPort: false asserts a real-world fact — the carrier port for this
  // number has completed. The server accepts it alongside isActive in one
  // PUT, and its activation guard passes when pendingPort: false is explicit
  // in that same request. Without this field there is no way to take an
  // imported number off the pending list from the app at all.
  updateNumber: (id: number, data: {
    campaignName?: string;
    forwardTo?: string | null;
    isActive?: boolean;
    recordCalls?: boolean;
    whisperEnabled?: boolean;
    pendingPort?: boolean;
  }) => api.put(`/numbers/${id}`, data),

  deleteNumber: (id: number) => api.delete(`/numbers/${id}`),

  getNumberCalls: (id: number, params?: {
    startDate?: string;
    endDate?: string;
  }) => api.get(`/numbers/${id}/calls`, { params }),
};

export const analyticsApi = {
  getDashboard: (params?: {
    startDate?: string;
    endDate?: string;
    campaign?: string;
  }) => api.get('/analytics/dashboard', { params }),

  getDailyReport: (startDate: string, endDate: string) =>
    api.get('/analytics/reports/daily', { params: { startDate, endDate } }),

  getSourcesReport: (startDate: string, endDate: string) =>
    api.get('/analytics/reports/sources', { params: { startDate, endDate } }),
};

export interface MakeCallResult {
  success: boolean;
  callSid: string;
  to: string;
  from: string;
  status: string;
}

// A CallRail import is previewed first (commit omitted/false) and, once the
// operator confirms the preview, re-sent with commit: true against the same
// credentials. The two calls return different shapes distinguished by `preview`.
export interface CallRailImportNumber {
  trackingNumber: string;
  name: string;
  // What CallRail has on file. NOT necessarily what the commit will write:
  // a forward_to already set here by hand wins, and rendering this value
  // unconditionally made the preview advertise a destination the commit
  // would not produce. Given this project's placeholder-forward_to history,
  // that is exactly the lie the preview exists to prevent.
  destinationNumber: string | null;
  // What the row will actually forward to once the commit lands.
  resultingDestination: string | null;
  // True when the local value won, i.e. resultingDestination came from here
  // and not from CallRail.
  keepsLocalForwardTo: boolean;
  action: 'create' | 'update';
}

export interface CallRailPreviewResult {
  preview: true;
  accountName: string;
  numbers: CallRailImportNumber[];
  callsToImport: number;
  warnings: string[];
}

export interface CallRailCommitResult {
  preview: false;
  accountName: string;
  numbersCreated: number;
  numbersUpdated: number;
  callsImported: number;
  warnings: string[];
}

export const twilioApi = {
  buyNumber: (data: {
    areaCode: string;
    campaignName: string;
    forwardTo: string;
    recordCalls?: boolean;
    whisperEnabled?: boolean;
  }) => api.post('/twilio/buy-number', data),

  makeCall: (data: {
    to: string;
    from: string;
  }) => api.post<MakeCallResult>('/twilio/make-call', data),

  importCallRail: (data: { apiKey: string; accountId: string; commit?: boolean }) =>
    api.post<CallRailPreviewResult | CallRailCommitResult>('/twilio/import-callrail', data),
};

export const authApi = {
  login: (username: string, password: string) =>
    api.post('/auth/login', { username, password }),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
};

export default api;